import { ip_back } from "../config";
import { normalizeAppLang, tourClipLang } from "../utils/appLanguages";

const MUTE_KEY = "pingflo:tourTtsMuted";

const AGENT_VOICE = {
  pingwi: { rate: 0.98, pitch: 1.06 },
  pingflo: { rate: 0.93, pitch: 1.02 },
  pingpro: { rate: 0.9, pitch: 0.98 },
  pingmynd: { rate: 0.88, pitch: 1.0 },
};

const SPEECH_LOCALE = {
  en: "en-GB",
  fr: "fr-FR",
  de: "de-DE",
  es: "es-ES",
  nl: "nl-NL",
  pt: "pt-PT",
};

const NAME_SAY = [
  [/\bPingmynd\b/gi, "Ping-mind"],
  [/\bPingpro\b/gi, "Ping-pro"],
  [/\bPingwi\b/gi, "Ping-wee"],
  [/\bPingflo\b/gi, "Ping-flow"],
];

const NOVELTY = /compact|eloquence|trinoids|zarvox|albert|bells|boing|bubbles|cellos|bad news|good news|organ|whisper|fred|junior|robot|novelty/i;
const NATURAL = /samantha|karen|moira|fiona|tessa|veena|martha|aria|jenny|natural|neural|premium|enhanced|google uk|google us|amelie|amélie|thomas|audrey|marie|google français|google francais|microsoft/i;

function clipStorageKey({ text, lang, agentId }) {
  return `v1|${agentId || "pingflo"}|${tourClipLang(lang)}|${text}`;
}

const IDB_NAME = "pingflo-tour-tts";
const IDB_STORE = "clips";

function openClipDb() {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbGetClip(key) {
  const db = await openClipDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, "readonly");
      const req = tx.objectStore(IDB_STORE).get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbPutClip(key, blob) {
  const db = await openClipDb();
  if (!db || !blob) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).put(blob, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

function staticTourAudioUrl(lineKey, lang) {
  if (!lineKey) return null;
  const safeLang = tourClipLang(lang);
  const safeKey = String(lineKey).replace(/[^a-z0-9_]/gi, "");
  if (!safeKey) return null;
  const base = (typeof process !== "undefined" && process.env && process.env.PUBLIC_URL) || "";
  return `${base}/tour-audio/${safeLang}/${safeKey}.wav`;
}

let staticManifestPromise = null;

function loadStaticManifest() {
  if (staticManifestPromise) return staticManifestPromise;
  const base = (typeof process !== "undefined" && process.env && process.env.PUBLIC_URL) || "";
  staticManifestPromise = fetch(`${base}/tour-audio/manifest.json`, { cache: "force-cache" })
    .then((res) => (res.ok ? res.json() : { files: [] }))
    .catch(() => ({ files: [] }));
  return staticManifestPromise;
}

async function fetchStaticTourAudio(lineKey, lang) {
  const url = staticTourAudioUrl(lineKey, lang);
  if (!url) return null;
  const manifest = await loadStaticManifest();
  const safeLang = tourClipLang(lang);
  const name = `${safeLang}/${lineKey}.wav`;
  if (!Array.isArray(manifest?.files) || !manifest.files.includes(name)) return null;
  try {
    const res = await fetch(url, { cache: "force-cache" });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob || blob.size < 44) return null;
    return blob;
  } catch {
    return null;
  }
}

export { clipStorageKey, staticTourAudioUrl, tourClipLang };

let speakGeneration = 0;
let currentAudio = null;
let currentObjectUrl = null;

function canSpeak() {
  return typeof window !== "undefined" && "speechSynthesis" in window && typeof SpeechSynthesisUtterance !== "undefined";
}

function stopGeminiAudio() {
  if (currentAudio) {
    try {
      currentAudio.pause();
      currentAudio.removeAttribute("src");
      currentAudio.load();
    } catch {
      /* ignore */
    }
    currentAudio = null;
  }
  if (currentObjectUrl) {
    try {
      URL.revokeObjectURL(currentObjectUrl);
    } catch {
      /* ignore */
    }
    currentObjectUrl = null;
  }
}

export function isTourTtsMuted() {
  try {
    return localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setTourTtsMuted(muted) {
  try {
    localStorage.setItem(MUTE_KEY, muted ? "1" : "0");
  } catch {
    /* ignore */
  }
  if (muted) cancelTourSpeech();
}

export function cancelTourSpeech() {
  speakGeneration += 1;
  stopGeminiAudio();
  if (!canSpeak()) return;
  try {
    window.speechSynthesis.cancel();
  } catch {
    /* ignore */
  }
}

let unlockedAudio = null;

export function speechLocale(lang) {
  const code = normalizeAppLang(lang);
  return SPEECH_LOCALE[code] || code;
}

export function armTourSpeech() {
  warmTourVoices();
  hushWebSpeech();
  if (typeof Audio === "undefined") return;
  if (!unlockedAudio) unlockedAudio = new Audio();
  try {
    unlockedAudio.muted = true;
    const play = unlockedAudio.play();
    if (play && typeof play.catch === "function") play.catch(() => {});
    unlockedAudio.pause();
    unlockedAudio.muted = false;
  } catch {
    /* ignore */
  }
  if (!canSpeak()) return;
  try {
    const warm = new SpeechSynthesisUtterance(" ");
    warm.volume = 0;
    warm.rate = 1;
    window.speechSynthesis.speak(warm);
  } catch {
    /* ignore */
  }
}

export function warmTourVoices() {
  if (!canSpeak()) return;
  try {
    window.speechSynthesis.getVoices();
  } catch {
    /* ignore */
  }
}

export function prepareSpokenText(raw) {
  let text = String(raw || "")
    .replace(/\*\*/g, "")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s*[—–]\s*/g, ", ")
    .replace(/\s+\/\s+/g, ", ")
    .replace(/\s+/g, " ")
    .trim();
  for (const [pattern, say] of NAME_SAY) {
    text = text.replace(pattern, say);
  }
  return text;
}

export function splitSpokenChunks(text) {
  const prepared = prepareSpokenText(text);
  if (!prepared) return [];
  const parts = [];
  let buf = "";
  for (let i = 0; i < prepared.length; i += 1) {
    const ch = prepared[i];
    buf += ch;
    if (ch === "." || ch === "!" || ch === "?") {
      const trimmed = buf.trim();
      if (trimmed) parts.push(trimmed);
      buf = "";
    }
  }
  const tail = buf.trim();
  if (tail) parts.push(tail);
  return parts;
}

export function scoreVoice(voice, lang) {
  if (!voice) return -999;
  const prefix = String(lang || "en").slice(0, 2).toLowerCase();
  const voiceLang = String(voice.lang || "").toLowerCase();
  if (!voiceLang.startsWith(prefix)) return -100;
  const name = String(voice.name || "");
  if (NOVELTY.test(name)) return -80;
  let score = 8;
  if (NATURAL.test(name)) score += 24;
  if (voice.localService) score += 4;
  if (/female|woman|samantha|karen|moira|aria|jenny|amelie|amélie|audrey/i.test(name)) score += 6;
  if (/en-gb|en_gb|fr-fr|fr_fr/i.test(voiceLang)) score += 3;
  return score;
}

export function pickVoice(lang) {
  try {
    const voices = window.speechSynthesis.getVoices() || [];
    if (!voices.length) return null;
    const ranked = [...voices].sort((a, b) => scoreVoice(b, lang) - scoreVoice(a, lang));
    if (scoreVoice(ranked[0], lang) > -50) return ranked[0];
    return ranked.find((voice) => !NOVELTY.test(String(voice.name || ""))) || ranked[0] || null;
  } catch {
    return null;
  }
}

async function fetchCachedServerTourAudio(lineKey, lang, agentId) {
  const token = localStorage.getItem("token");
  if (!token || !lineKey) return null;
  const safeLang = tourClipLang(lang);
  const safeKey = String(lineKey).replace(/[^a-z0-9_]/gi, "");
  if (!safeKey) return null;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 6000);
  try {
    const res = await fetch(
      `${ip_back}api/ai-coach/tts/${encodeURIComponent(safeLang)}/${encodeURIComponent(safeKey)}?agentId=${encodeURIComponent(agentId || "pingflo")}`,
      {
        method: "GET",
        headers: { Authorization: token },
        signal: controller.signal,
      }
    );
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob || blob.size < 44) return null;
    return blob;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

export function isGeminiTtsQuotaMessage(text) {
  return /exceed|quota|resource.?exhausted|too many requests|429|number of (calls|requests)/i.test(
    String(text || "")
  );
}

let geminiPostBlockedUntil = 0;
const GEMINI_TTS_COOLDOWN_MS = 10 * 60 * 1000;

async function fetchGeminiTourAudio(text, { lang, agentId, lineKey }) {
  if (Date.now() < geminiPostBlockedUntil) throw new Error("tts paused");
  const token = localStorage.getItem("token");
  if (!token) throw new Error("no token");
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 15000);
  try {
    const res = await fetch(`${ip_back}api/ai-coach/tts`, {
      method: "POST",
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ text, lang: tourClipLang(lang), agentId, lineKey }),
      signal: controller.signal,
    });
    if (!res.ok) {
      let body = "";
      try {
        body = await res.text();
      } catch {
        body = "";
      }
      if (res.status === 429 || isGeminiTtsQuotaMessage(body)) {
        geminiPostBlockedUntil = Date.now() + GEMINI_TTS_COOLDOWN_MS;
      }
      throw new Error(`tts ${res.status}`);
    }
    const blob = await res.blob();
    if (!blob || blob.size < 44) throw new Error("empty audio");
    return blob;
  } finally {
    window.clearTimeout(timer);
  }
}

function hushWebSpeech() {
  if (!canSpeak()) return;
  try {
    window.speechSynthesis.cancel();
  } catch {
    /* ignore */
  }
}

function speakWithWebSpeech(chunks, { lang, agentId, generation }) {
  return new Promise((resolve) => {
    if (!canSpeak() || !chunks.length || generation !== speakGeneration) {
      resolve(false);
      return;
    }
    const profile = AGENT_VOICE[agentId] || AGENT_VOICE.pingflo;
    const langTag = speechLocale(lang);
    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      resolve(Boolean(ok) && generation === speakGeneration);
    };

    const speakChunk = (index) => {
      if (generation !== speakGeneration) {
        finish(false);
        return;
      }
      if (index >= chunks.length) {
        finish(true);
        return;
      }
      const utterance = new SpeechSynthesisUtterance(chunks[index]);
      utterance.lang = langTag;
      const voice = pickVoice(langTag);
      if (voice) utterance.voice = voice;
      utterance.rate = profile.rate;
      utterance.pitch = profile.pitch;
      utterance.volume = 0.96;
      utterance.onend = () => {
        if (generation !== speakGeneration) {
          finish(false);
          return;
        }
        window.setTimeout(() => speakChunk(index + 1), 220);
      };
      utterance.onerror = () => {
        if (generation !== speakGeneration) {
          finish(false);
          return;
        }
        window.setTimeout(() => speakChunk(index + 1), 80);
      };
      try {
        window.speechSynthesis.speak(utterance);
      } catch {
        finish(false);
      }
    };

    const start = () => {
      if (settled) return;
      speakChunk(0);
    };
    if ((window.speechSynthesis.getVoices() || []).length) {
      start();
      return;
    }
    const onVoices = () => {
      window.speechSynthesis.removeEventListener("voiceschanged", onVoices);
      start();
    };
    window.speechSynthesis.addEventListener("voiceschanged", onVoices);
    window.setTimeout(start, 250);
  });
}

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function tourCaptionHoldMs(text) {
  const words = String(text || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.min(10000, Math.max(2800, words * 220));
}

async function holdWhileCurrent(generation, ms) {
  const started = Date.now();
  while (Date.now() - started < ms) {
    if (generation !== speakGeneration) return false;
    await delay(80);
  }
  return generation === speakGeneration;
}

function waitForAudioEnd(audio, generation) {
  return new Promise((resolve) => {
    let settled = false;
    let timer = 0;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      if (audio) {
        audio.removeEventListener("ended", onEnded);
        audio.removeEventListener("error", onError);
      }
      resolve(Boolean(ok) && generation === speakGeneration);
    };
    const onEnded = () => finish(true);
    const onError = () => finish(false);
    if (!audio || generation !== speakGeneration) {
      resolve(false);
      return;
    }
    if (audio.ended) {
      resolve(generation === speakGeneration);
      return;
    }
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    const dur = Number(audio.duration);
    const waitMs =
      Number.isFinite(dur) && dur > 0 ? Math.min(dur * 1000 + 400, 45000) : 30000;
    timer = window.setTimeout(() => finish(true), waitMs);
  });
}

async function playBlob(blob, generation) {
  if (generation !== speakGeneration) return false;
  const type = String(blob?.type || "");
  if (type && !type.startsWith("audio") && type !== "application/octet-stream") return false;
  const url = URL.createObjectURL(blob);
  currentObjectUrl = url;
  const audio = unlockedAudio || new Audio();
  unlockedAudio = audio;
  currentAudio = audio;
  audio.muted = false;
  audio.src = url;
  audio.onended = () => {
    if (currentObjectUrl === url) {
      URL.revokeObjectURL(url);
      currentObjectUrl = null;
    }
  };
  try {
    await audio.play();
    if (generation !== speakGeneration) return false;
    return waitForAudioEnd(audio, generation);
  } catch {
    return false;
  }
}

export function speakTourText(text, { lang = "en", agentId = "pingflo", lineKey } = {}) {
  speakGeneration += 1;
  const generation = speakGeneration;
  stopGeminiAudio();
  hushWebSpeech();
  const prepared = prepareSpokenText(text);
  if (!prepared) return Promise.resolve(false);
  if (isTourTtsMuted()) return holdWhileCurrent(generation, tourCaptionHoldMs(prepared));
  const clipLang = tourClipLang(lang);
  const speechLang = normalizeAppLang(lang);
  const storageKey = clipStorageKey({ text: prepared, lang: clipLang, agentId });
  const chunks = splitSpokenChunks(prepared);

  return (async () => {
    const takeBlob = async (blob) => {
      if (!blob || generation !== speakGeneration) return false;
      hushWebSpeech();
      const played = await playBlob(blob, generation);
      return Boolean(played) && generation === speakGeneration;
    };
    try {
      const skipLineCache = [
        "tour_admin_invite_body",
        "tour_player_session_body",
        "tour_player_calendar_body",
        "tour_player_smart_body",
        "tour_coach_smart_body",
        "tour_admin_smart_body",
        "tour_parent_smart_body",
        "tour_player_tech_body",
        "tour_live_join_body",
      ].includes(String(lineKey || ""));
      if (!skipLineCache) {
        const staticBlob = await fetchStaticTourAudio(lineKey, clipLang);
        if (await takeBlob(staticBlob)) {
          await idbPutClip(storageKey, staticBlob);
          return true;
        }
      }
      const cached = await idbGetClip(storageKey);
      if (await takeBlob(cached)) return true;
      if (!skipLineCache) {
        const serverCached = await fetchCachedServerTourAudio(lineKey, clipLang, agentId);
        if (await takeBlob(serverCached)) {
          await idbPutClip(storageKey, serverCached);
          return true;
        }
      }
      if (generation !== speakGeneration) return false;
      const blob = await fetchGeminiTourAudio(prepared, { lang: clipLang, agentId, lineKey });
      if (await takeBlob(blob)) {
        await idbPutClip(storageKey, blob);
        return true;
      }
    } catch {
      /* Gemini / network — fall through to the browser voice */
    }
    if (generation !== speakGeneration) return false;
    hushWebSpeech();
    const spoken = await speakWithWebSpeech(chunks, { lang: speechLang, agentId, generation });
    if (spoken) return true;
    if (generation !== speakGeneration) return false;
    return holdWhileCurrent(generation, tourCaptionHoldMs(prepared));
  })();
}
